import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Phone, Mic, MessageSquare, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TestingUtils } from "@/utils/testing";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CallFlowTestSuiteProps {
  onTestResult: (result: any) => void;
  onTestLog: (log: any) => void;
  isRunning: boolean;
  setIsRunning: (running: boolean) => void;
}

export interface CallFlowTestSuiteRef {
  runAllTests: () => Promise<void>;
}

/**
 * Advanced Call Flow Testing Suite
 * Tests voice webhooks, call recording, transcription, AI analysis, and SMS notifications
 */
export const CallFlowTestSuite = forwardRef<CallFlowTestSuiteRef, CallFlowTestSuiteProps>(({
  onTestResult,
  onTestLog,
  isRunning,
  setIsRunning
}, ref) => {
  const { activeTenantId } = useAuth();
  const [selectedTest, setSelectedTest] = useState<string>("");
  const [currentTest, setCurrentTest] = useState<string>("");
  const [testPayload, setTestPayload] = useState<string>("");

  // Expose runAllTests to parent via ref
  const runAllCallFlowTestsRef = React.useRef<(() => Promise<void>) | null>(null);
  
  useImperativeHandle(ref, () => ({
    runAllTests: async () => {
      if (runAllCallFlowTestsRef.current) {
        await runAllCallFlowTestsRef.current();
      }
    }
  }), []);
  /**
   * Test configurations with pre-built, realistic payloads
   */
  const getTestConfigurations = useCallback(() => {
    const testPhone = TestingUtils.generateTestPhoneNumber();
    const testCallSid = `CA${Math.random().toString(36).substr(2, 32)}`;
    const testRecordingSid = `RE${Math.random().toString(36).substr(2, 32)}`;

    return {
      'voice-webhooks': {
        name: 'Voice Webhooks',
        description: 'Test incoming call handling and TwiML response',
        payload: {
          From: testPhone,
          To: TestingUtils.generateTestPhoneNumber(),
          CallSid: testCallSid,
          CallStatus: 'in-progress',
          Direction: 'inbound',
          CallerName: 'Test Caller',
          CallerCity: 'San Francisco',
          CallerState: 'CA',
          CallerCountry: 'US'
        }
      },
      'call-recording': {
        name: 'Call Recording',
        description: 'Test recording status updates and storage',
        payload: {
          RecordingSid: testRecordingSid,
          CallSid: testCallSid,
          RecordingUrl: 'https://api.twilio.com/2010-04-01/Accounts/test/Recordings/test.mp3',
          RecordingDuration: '45',
          RecordingStatus: 'completed',
          RecordingChannels: '1',
          RecordingSource: 'RecordVerb'
        }
      },
      'transcription': {
        name: 'Transcription',
        description: 'Test call transcription and text extraction',
        payload: {
          callId: `test_call_${Date.now()}`,
          recordingUrl: 'https://api.twilio.com/2010-04-01/Accounts/test/Recordings/test.mp3',
          recordingSid: testRecordingSid,
          duration: 45
        }
      },
      'ai-analysis': {
        name: 'AI Analysis',
        description: 'Test AI-powered call summarization and insights',
        payload: {
          callId: `test_call_${Date.now()}`,
          transcript: "Hello, I'm calling about your services. I'm interested in learning more about your pricing and features. Can you help me understand what options are available? I'm particularly interested in the enterprise plan and whether you offer custom integrations.",
          duration: 120,
          fromNumber: testPhone,
          toNumber: TestingUtils.generateTestPhoneNumber(),
          timestamp: new Date().toISOString()
        }
      },
      'sms-notifications': {
        name: 'SMS Notifications',
        description: 'Test missed call SMS alerts',
        payload: {
          FromNumber: testPhone,
          ToNumber: TestingUtils.generateTestPhoneNumber(),
          CallSid: testCallSid,
          CallStatus: 'no-answer',
          CallDuration: '0',
          CallerName: 'Test Caller',
          MissedCallTime: new Date().toISOString()
        }
      },
      'complete-flow': {
        name: 'Complete Call Flow',
        description: 'End-to-end test of the entire call processing pipeline',
        payload: {
          CallSid: testCallSid,
          From: testPhone,
          To: TestingUtils.generateTestPhoneNumber(),
          CallStatus: 'completed',
          CallDuration: '120',
          RecordingSid: testRecordingSid,
          RecordingUrl: 'https://api.twilio.com/2010-04-01/Accounts/test/Recordings/test.mp3',
          Direction: 'inbound',
          CallerName: 'Test Caller'
        }
      }
    };
  }, []);

  // Auto-populate payload when selected test changes
  useEffect(() => {
    if (!selectedTest) return;
    
    const testConfigs = getTestConfigurations();
    const config = testConfigs[selectedTest as keyof typeof testConfigs];
    
    if (config) {
      setTestPayload(JSON.stringify(config.payload, null, 2));
    }
  }, [selectedTest, getTestConfigurations]);

  /**
   * Helper function to log test events
   */
  const log = useCallback((level: "info" | "warn" | "error" | "success", message: string, details?: any) => {
    console.log(`[CALL_FLOW] ${level.toUpperCase()}: ${message}`, details);
    onTestLog({
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      details,
      category: "call_flow"
    });
  }, [onTestLog]);

  /**
   * Create a standardized test result
   */
  const createTestResult = useCallback((name: string, success: boolean, details?: string, error?: string) => {
    const now = new Date();
    return {
      id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      testName: name, // Primary field
      name, // For backward compatibility
      status: success ? "success" : "failure" as const,
      startTime: now,
      endTime: now,
      duration: 0,
      details,
      error,
      errorMessage: error, // For TestingComponent compatibility
      category: "call_flow"
    };
  }, []);

  /**
   * Test voice webhook endpoints with comprehensive error handling
   * @returns Test result with performance metrics
   */
  const testVoiceWebhooks = async () => {
    log("info", "Testing voice webhook endpoints...");
    
    try {
      // Test the voice webhook endpoint
      const result = await TestingUtils.testEdgeFunction("Calie_voice", {
        From: TestingUtils.generateTestPhoneNumber(),
        To: TestingUtils.generateTestPhoneNumber(),
        CallSid: `CA${Math.random().toString(36).substr(2, 32)}`,
        CallStatus: "in-progress"
      });

      if (result.success) {
        log("success", "Voice webhook test completed successfully", result);
        return createTestResult("Voice Webhook Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "Voice webhook test failed", result);
        return createTestResult("Voice Webhook Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Voice webhook test exception", { error: errorMessage });
      return createTestResult("Voice Webhook Test", false, undefined, errorMessage);
    }
  };

  /**
   * Test call recording functionality
   */
  const testCallRecording = async () => {
    log("info", "Testing call recording functionality...");
    
    try {
      // Fetch a real call with a twilio_sid to test against
      const { data: existingCall } = await supabase
        .from('calie_calls')
        .select('twilio_sid')
        .eq('tenant_id', activeTenantId || '')
        .not('twilio_sid', 'is', null)
        .limit(1)
        .maybeSingle();

      if (!existingCall?.twilio_sid) {
        log("info", "No existing calls with twilio_sid found for this tenant; skipping recording test.");
        return createTestResult(
          "Call Recording Test",
          true,
          "Skipped: No existing calls with twilio_sid for this tenant"
        );
      }

      const callSid = existingCall.twilio_sid;

      // Test the recording webhook endpoint using a real CallSid
      const result = await TestingUtils.testEdgeFunction("Calie_handleRecording", {
        RecordingSid: `RE${Math.random().toString(36).substr(2, 12)}`,
        CallSid: callSid,
        RecordingUrl: "https://api.twilio.com/2010-04-01/Accounts/test/Recordings/test.mp3",
        RecordingDuration: "45"
      });

      if (result.success) {
        log("success", "Call recording test completed successfully", result);
        return createTestResult("Call Recording Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "Call recording test failed", result);
        return createTestResult("Call Recording Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Call recording test exception", { error: errorMessage });
      return createTestResult("Call Recording Test", false, undefined, errorMessage);
    }
  };

  /**
   * Test transcription functionality
   */
  const testTranscription = async () => {
    log("info", "Testing call transcription...");
    
    try {
      // Create a test call record first
      const testCallId = `test_call_${Date.now()}`;
      
      const result = await TestingUtils.testEdgeFunction("Calie_transcribe", {
        callId: testCallId,
        recordingUrl: "https://api.twilio.com/2010-04-01/Accounts/test/Recordings/test.mp3"
      });

      if (result.success) {
        log("success", "Transcription test completed successfully", result);
        return createTestResult("Transcription Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "Transcription test failed", result);
        return createTestResult("Transcription Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Transcription test exception", { error: errorMessage });
      return createTestResult("Transcription Test", false, undefined, errorMessage);
    }
  };

  /**
   * Test AI analysis and summarization
   */
  const testAIAnalysis = async () => {
    log("info", "Testing AI analysis and summarization...");
    
    try {
      const result = await TestingUtils.testEdgeFunction("Calie_summarize", {
        callId: `test_call_${Date.now()}`,
        transcript: "Hello, I'm calling about your services. I'm interested in learning more about your pricing and features. Can you help me understand what options are available?"
      });

      if (result.success) {
        log("success", "AI analysis test completed successfully", result);
        return createTestResult("AI Analysis Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "AI analysis test failed", result);
        return createTestResult("AI Analysis Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "AI analysis test exception", { error: errorMessage });
      return createTestResult("AI Analysis Test", false, undefined, errorMessage);
    }
  };

  /**
   * Test SMS notifications for missed calls
   */
  const testSMSNotifications = async () => {
    log("info", "Testing SMS notifications for missed calls...");
    
    try {
      const result = await TestingUtils.testEdgeFunction("Calie_missedCallSMS", {
        FromNumber: TestingUtils.generateTestPhoneNumber(),
        ToNumber: TestingUtils.generateTestPhoneNumber(),
        CallSid: `CA${Math.random().toString(36).substr(2, 32)}`,
        CallStatus: "no-answer"
      });

      if (result.success) {
        log("success", "SMS notification test completed successfully", result);
        return createTestResult("SMS Notification Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "SMS notification test failed", result);
        return createTestResult("SMS Notification Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "SMS notification test exception", { error: errorMessage });
      return createTestResult("SMS Notification Test", false, undefined, errorMessage);
    }
  };

  /**
   * Test complete call processing flow
   */
  const testCompleteCallFlow = async () => {
    log("info", "Testing complete call processing flow...");
    
    try {
      const result = await TestingUtils.testEdgeFunction("Calie_processCallComplete", {
        CallSid: `CA${Math.random().toString(36).substr(2, 32)}`,
        From: TestingUtils.generateTestPhoneNumber(),
        To: TestingUtils.generateTestPhoneNumber(),
        CallStatus: "completed",
        CallDuration: "120",
        RecordingSid: `RE${Math.random().toString(36).substr(2, 32)}`
      });

      if (result.success) {
        log("success", "Complete call flow test completed successfully", result);
        return createTestResult("Complete Call Flow Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`);
      } else {
        log("error", "Complete call flow test failed", result);
        return createTestResult("Complete Call Flow Test", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Complete call flow test exception", { error: errorMessage });
      return createTestResult("Complete Call Flow Test", false, undefined, errorMessage);
    }
  };

  /**
   * Run all call flow tests
   */
  const runAllCallFlowTests = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    log("info", "Starting comprehensive call flow testing...");

    const tests = [
      { name: "Voice Webhooks", fn: testVoiceWebhooks },
      { name: "Call Recording", fn: testCallRecording },
      { name: "Transcription", fn: testTranscription },
      { name: "AI Analysis", fn: testAIAnalysis },
      { name: "SMS Notifications", fn: testSMSNotifications },
      { name: "Complete Call Flow", fn: testCompleteCallFlow }
    ];

    let passed = 0;
    let failed = 0;

    for (const test of tests) {
      try {
        setCurrentTest(test.name);
        log("info", `Running ${test.name} test...`);
        
        const result = await test.fn();
        onTestResult(result);
        
        if (result.status === "success") {
          passed++;
        } else {
          failed++;
        }
        
        // Wait between tests
        await TestingUtils.sleep(500);
      } catch (error) {
        failed++;
        const errorMessage = error instanceof Error ? error.message : String(error);
        log("error", `Test ${test.name} failed with exception: ${errorMessage}`);
        onTestResult(createTestResult(test.name, false, undefined, errorMessage));
      }
    }

    setCurrentTest("");
    setIsRunning(false);
    
    log("success", `Call flow testing completed. Passed: ${passed}, Failed: ${failed}`);
    onTestResult(createTestResult(
      "Call Flow Test Suite", 
      failed === 0, 
      `Completed ${tests.length} tests. Passed: ${passed}, Failed: ${failed}`
    ));
  };

  // Assign runAllCallFlowTests to ref for imperative handle
  React.useEffect(() => {
    runAllCallFlowTestsRef.current = runAllCallFlowTests;
  }, [runAllCallFlowTests]);

  /**
   * Run selected test
   */
  const runSelectedTest = async () => {
    if (!selectedTest || isRunning) return;
    
    setIsRunning(true);
    setCurrentTest(selectedTest);
    
    const testFunctions: Record<string, () => Promise<any>> = {
      'voice-webhooks': testVoiceWebhooks,
      'call-recording': testCallRecording,
      'transcription': testTranscription,
      'ai-analysis': testAIAnalysis,
      'sms-notifications': testSMSNotifications,
      'complete-flow': testCompleteCallFlow
    };

    try {
      const testFn = testFunctions[selectedTest];
      if (testFn) {
        const result = await testFn();
        onTestResult(result);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", `Test failed: ${errorMessage}`);
      onTestResult(createTestResult(selectedTest, false, undefined, errorMessage));
    }

    setCurrentTest("");
    setIsRunning(false);
  };

  /**
   * Test custom call flow with user payload
   */
  const testCustomCallFlow = async () => {
    if (!testPayload || isRunning) return;
    
    setIsRunning(true);
    setCurrentTest("Custom Call Flow");
    
    try {
      const payload = JSON.parse(testPayload);
      const result = await TestingUtils.testEdgeFunction("Calie_voice", payload);
      
      if (result.success) {
        log("success", "Custom call flow test completed successfully", result);
        onTestResult(createTestResult("Custom Call Flow Test", true, `Response time: ${result.responseTime.toFixed(2)}ms`));
      } else {
        log("error", "Custom call flow test failed", result);
        onTestResult(createTestResult("Custom Call Flow Test", false, undefined, result.error));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Custom call flow test exception", { error: errorMessage });
      onTestResult(createTestResult("Custom Call Flow Test", false, undefined, errorMessage));
    }

    setCurrentTest("");
    setIsRunning(false);
  };

  return (
    <div className="space-y-6">
      {/* Call Flow Test Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Advanced Call Flow Testing
          </CardTitle>
          <CardDescription>
            Test voice webhooks, call recording, transcription, AI analysis, and SMS notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedTest} onValueChange={setSelectedTest}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select a specific test" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="voice-webhooks">Voice Webhooks</SelectItem>
                <SelectItem value="call-recording">Call Recording</SelectItem>
                <SelectItem value="transcription">Transcription</SelectItem>
                <SelectItem value="ai-analysis">AI Analysis</SelectItem>
                <SelectItem value="sms-notifications">SMS Notifications</SelectItem>
                <SelectItem value="complete-flow">Complete Call Flow</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={runSelectedTest} 
              disabled={!selectedTest || isRunning}
              variant="outline"
            >
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              Test Selected
            </Button>
            
            <Button 
              onClick={runAllCallFlowTests} 
              disabled={isRunning}
              className="bg-gradient-to-r from-primary to-primary/80"
            >
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
              Test All Call Flows
            </Button>
          </div>

          {isRunning && currentTest && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Running</Badge>
              <span className="text-sm text-muted-foreground">Currently testing: {currentTest}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Call Flow Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-accent" />
            Custom Call Flow Testing
          </CardTitle>
          <CardDescription>
            Test custom call flows with ready-to-use webhook payloads
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Test Payload (JSON)</label>
              {selectedTest && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Zap className="h-3 w-3 mr-1" />
                    Auto-populated
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const testConfigs = getTestConfigurations();
                      const config = testConfigs[selectedTest as keyof typeof testConfigs];
                      if (config) {
                        setTestPayload(JSON.stringify(config.payload, null, 2));
                      }
                    }}
                    className="h-7 text-xs"
                  >
                    Reset to Default
                  </Button>
                </div>
              )}
            </div>
            <Textarea
              placeholder='{"From": "+15551234567", "To": "+15559876543", "CallSid": "CAxxxxxxxxx", "CallStatus": "in-progress"}'
              value={testPayload}
              onChange={(e) => setTestPayload(e.target.value)}
              className="min-h-[100px] font-mono text-sm"
            />
            <div className="text-xs text-muted-foreground">
              {selectedTest 
                ? `Ready-to-test payload for ${getTestConfigurations()[selectedTest as keyof ReturnType<typeof getTestConfigurations>]?.name || 'selected test'}`
                : 'Select a test above to auto-populate this field with a realistic payload'
              }
            </div>
          </div>
          
          <Button 
            onClick={testCustomCallFlow} 
            disabled={!testPayload || isRunning}
            variant="outline"
          >
            {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
            Test Custom Flow
          </Button>
        </CardContent>
      </Card>

      {/* Test Coverage Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Call Flow Test Coverage</CardTitle>
          <CardDescription>
            Overview of what each test validates in the call handling pipeline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" />
                <span className="font-medium">Voice Webhooks</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Tests incoming call handling, TwiML responses, and call routing logic
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-primary" />
                <span className="font-medium">Call Recording</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Validates recording webhook processing and storage integration
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                <span className="font-medium">Transcription</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Tests speech-to-text processing and transcript storage
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <span className="font-medium">AI Analysis</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Validates sentiment analysis, summarization, and insights generation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

CallFlowTestSuite.displayName = 'CallFlowTestSuite';